import { useMutation, useQueryClient } from "@tanstack/react-query";
import { DateTime } from "luxon";

import apiClient from "@app/common/api/apiClient";
import type { SessionInfo } from "@app/common/types";

export interface SessionMetadataUpdateBody {
  sessionId: string;
  startTime?: DateTime;
  title?: string;
}

const useUpdateSessionMetadata = () => {
  const queryClient = useQueryClient();

  const updateSessionMetadataMutation = useMutation({
    mutationFn: async ({ sessionId, ...body }: SessionMetadataUpdateBody) =>
      await apiClient.session.updateSessionInfo(sessionId, body),
    onMutate: async ({
      sessionId,
      ...updateBody
    }: SessionMetadataUpdateBody) => {
      await queryClient.cancelQueries({
        queryKey: ["sessionInfo", sessionId],
      });

      const previousMeetingInfo = queryClient.getQueryData([
        "sessionInfo",
        sessionId,
      ]) as SessionInfo;

      const updatedSession: Partial<SessionInfo> = {};

      if (updateBody.title) {
        updatedSession.title = updateBody.title;
      }

      if (updateBody.startTime && previousMeetingInfo.endTime) {
        const duration = DateTime.fromISO(previousMeetingInfo.endTime).diff(
          DateTime.fromISO(previousMeetingInfo.startTime)
        );
        const newStartTime =
          updateBody.startTime.toISO() ?? previousMeetingInfo.startTime;
        const newEndTime = updateBody.startTime.plus(duration).toISO();

        updatedSession.startTime = newStartTime;
        updatedSession.endTime = newEndTime;
      }

      queryClient.setQueryData(["sessionInfo", sessionId], (old) => {
        const oldSessionInfo = old as SessionInfo;
        return {
          ...oldSessionInfo,
          ...updatedSession,
        };
      });
    },
    onSuccess: (result, { sessionId }) => {
      queryClient.invalidateQueries({ queryKey: ["sessionInfo", sessionId] });
      queryClient.invalidateQueries({ queryKey: ["sessionsList"] });
    },
  });

  return { updateSessionMetadata: updateSessionMetadataMutation };
};

export default useUpdateSessionMetadata;
